/**
 * NewsFeed — the "front page": persisted, department- and contact-tagged news
 * about California agencies, defaulting to the caller's accounts.
 *
 * WHO it is about is the global header's business, same as every other data
 * page: the selected agency wins, else the active account list, else all
 * agencies (Ryan 2026-09-08: "use the same filtering bubbles as all the
 * other pages"). The page keeps only what the header cannot say — lane,
 * categories, search, recency — as chips + one search box (GAS-shell
 * parity). Department chips on an item select that agency in the header;
 * category counts come from the same window/scope as the list so a chip
 * never promises items the list doesn't have. Lane/category/search/since
 * round-trip through the URL; a legacy ?departments=CDT deep link is
 * absorbed into the header selection on arrival.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Newspaper, Search, RefreshCw, AlertCircle, Building2, ListChecks, Layers } from 'lucide-react';
import { NEWS_CATEGORY_OPTIONS } from '@techstack/core';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useListsContext } from '../../contexts/ListsContext';
import useNewsFeed, { SINCE_OPTIONS, NEWS_LANE_OPTIONS } from './useNewsFeed';
import NewsItemRow from './NewsItemRow';
import '../shared/shared-chrome.css';
import './NewsFeed.css';

function dayKey(iso) {
  if (!iso) return 'Undated';
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: diff > 300 ? 'numeric' : undefined });
}

export default function NewsFeed({ departments = [], selectedDepartment = null, onDepartmentSelect }) {
  useDocumentTitle('News');
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedList, selectedDepartmentIds } = useListsContext();
  const headerScope = useMemo(() => ({
    departmentId: selectedDepartment?.department_id ?? null,
    listDepartmentIds: selectedDepartmentIds,
  }), [selectedDepartment?.department_id, selectedDepartmentIds]);
  const feed = useNewsFeed(location.search, headerScope);
  const { filters, set, toggleCategory, items, counts, isLoading, isFetching, error, hasMore, loadMore, refresh, notice, search } = feed;

  // Legacy deep link (/news?departments=CDT from the Home strip or an account
  // page): promote it to the header's agency selection once the department
  // list is available, then let the URL sync below drop the param.
  const legacyDept = useRef(new URLSearchParams(location.search).get('departments'));
  useEffect(() => {
    const raw = legacyDept.current;
    if (!raw || !departments?.length || !onDepartmentSelect) return;
    legacyDept.current = null;
    const key = raw.split(',')[0].trim().toLowerCase();
    const d = departments.find((x) => String(x.department_id) === key || (x.acronym || '').toLowerCase() === key);
    if (d) onDepartmentSelect(d.department_id);
  }, [departments, onDepartmentSelect]);

  // Keep the URL in step with the filters (replace, not push — chips are not history).
  useEffect(() => {
    if (location.search !== search) navigate({ pathname: location.pathname, search }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const grouped = useMemo(() => {
    const groups = [];
    let cur = null;
    for (const it of items) {
      const k = dayKey(it.published_at || it.fetched_at);
      if (!cur || cur.key !== k) { cur = { key: k, items: [] }; groups.push(cur); }
      cur.items.push(it);
    }
    return groups;
  }, [items]);

  const deptMatch = filters.lane === 'agencies' && selectedDepartment?.acronym ? selectedDepartment : null;
  const listScoped = !deptMatch && filters.lane === 'agencies' && !!selectedList && (selectedDepartmentIds?.length || 0) > 0;

  const openAccount = (d) => {
    if (!onDepartmentSelect) return;
    onDepartmentSelect(d.department_id);
    navigate('/workspace');
  };

  return (
    <div className="nf-page">
      <header className="ts-page-header nf-header">
        <div>
          <h1 className="ts-page-title"><Newspaper size={20} /> News</h1>
          <p className="ts-page-blurb">
            What changed at your agencies — leadership, budgets, procurements, incidents — tagged to the departments and people in your CRM.
          </p>
        </div>
        <div className="ts-page-actions">
          {deptMatch && (
            <>
              <button type="button" className="nf-btn" onClick={() => openAccount(deptMatch)}>Open {deptMatch.acronym} workspace</button>
              <button
                type="button"
                className="nf-btn nf-btn--primary"
                disabled={refresh.isPending}
                onClick={() => refresh.mutate(deptMatch.acronym)}
                title="Pull Google News + this agency's newsroom now"
              >
                <RefreshCw size={13} className={refresh.isPending ? 'nf-spin' : ''} /> Refresh {deptMatch.acronym}
              </button>
            </>
          )}
        </div>
      </header>

      <div className="nf-filters">
        <div className="nf-filter-row">
          <div className="nf-chips" role="tablist" aria-label="Lane">
            {NEWS_LANE_OPTIONS.map(([k, label, hint]) => (
              <button key={k} type="button" role="tab" aria-selected={filters.lane === k}
                className={`nf-chip nf-chip--scope ${filters.lane === k ? 'nf-chip--active' : ''}`}
                title={hint} onClick={() => set({ lane: k })}>
                {label}
              </button>
            ))}
          </div>
          {filters.lane === 'agencies' && (
            <span className="nf-scope-hint" title="Change the agency or account list in the header above — the same filter every page uses.">
              {deptMatch ? (
                <><Building2 size={12} /> {deptMatch.acronym}</>
              ) : listScoped ? (
                <><ListChecks size={12} /> {selectedList.name} · {selectedDepartmentIds.length} agencies</>
              ) : (
                <><Layers size={12} /> All agencies</>
              )}
            </span>
          )}
          <label className="nf-since">
            <span>Last</span>
            <select value={filters.since} onChange={(e) => set({ since: e.target.value })}>
              {SINCE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>
        <div className="nf-filter-row">
          <div className="nf-search">
            <Search size={14} className="nf-search-icon" />
            <input
              type="search"
              placeholder="Search headlines, people, vendors…"
              value={filters.q}
              onChange={(e) => set({ q: e.target.value })}
            />
          </div>
        </div>
        <div className="nf-chips nf-chips--cats">
          {NEWS_CATEGORY_OPTIONS.map(([k, label, hint]) => {
            const n = counts.by_category?.[k] || 0;
            const on = filters.categories.includes(k);
            return (
              <button key={k} type="button" className={`nf-chip nf-chip--cat nf-cat--${k} ${on ? 'nf-chip--active' : ''} ${!n && !on ? 'nf-chip--empty' : ''}`}
                title={hint} onClick={() => toggleCategory(k)}>
                {label}{n ? <span className="nf-chip-count">{n}</span> : null}
              </button>
            );
          })}
          {filters.categories.length > 0 && (
            <button type="button" className="nf-chip nf-chip--clear" onClick={() => set({ categories: [] })}>Clear</button>
          )}
        </div>
      </div>

      {notice && <p className={`nf-notice nf-notice--${notice.kind}`}>{notice.text}</p>}
      {error && <div className="nf-error"><AlertCircle size={14} /> {error.message}</div>}

      {isLoading ? (
        <div className="nf-empty"><RefreshCw size={18} className="nf-spin" /> Loading news…</div>
      ) : items.length === 0 ? (
        <div className="nf-empty"><p>Nothing in this window. Widen the time range or clear a filter.</p></div>
      ) : (
        <div className={`nf-list ${isFetching ? 'nf-list--stale' : ''}`}>
          <div className="nf-count">{counts.total_window} item(s) in window</div>
          {grouped.map((g) => (
            <section key={g.key} className="nf-day">
              <h2 className="ts-section-label nf-day-label">{g.key}</h2>
              {g.items.map((it) => (
                <NewsItemRow
                  key={it.id}
                  item={it}
                  onDepartment={(d) => { if (onDepartmentSelect) onDepartmentSelect(d.department_id); set({ lane: 'agencies' }); }}
                  onContact={(c) => set({ q: c.name })}
                  onCategory={(c) => (filters.categories.includes(c) ? null : toggleCategory(c))}
                />
              ))}
            </section>
          ))}
          {hasMore && (
            <button type="button" className="nf-btn nf-more" onClick={loadMore}>Load older</button>
          )}
        </div>
      )}
    </div>
  );
}
